import { yupResolver } from '@hookform/resolvers/yup'
import CancelIcon from '@mui/icons-material/Cancel'
import ModeEditIcon from '@mui/icons-material/ModeEdit'
import SaveAsIcon from '@mui/icons-material/SaveAs'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import { Permission } from 'interface'
import { useSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import axiosClient from 'utils/axiosClient'
import * as yup from 'yup'

type Props = {
  apiEndpoint?: string
  label: string
  permission: Permission
}
interface FormInput {
  permission: Permission
}

export default function EditPermission({
  apiEndpoint,
  label,
  permission,
}: Props) {
  const { enqueueSnackbar } = useSnackbar()
  const schema = yup.object()
  const {
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      permission,
    },
  })
  const [loading, setLoading] = useState(false)
  const [edit, setEdit] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const handleKeyDown = (e: globalThis.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setEdit(false)
      setValue('permission', permission, {
        shouldValidate: true,
      })
    }
  }
  useEffect(() => {
    if (ref.current) {
      if (edit) {
        ref.current.addEventListener('keydown', (e) => {
          handleKeyDown(e)
        })
      } else {
        ref.current.removeEventListener('keydown', (e) => {
          handleKeyDown(e)
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit])
  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    if (apiEndpoint) {
      setLoading(true)
      try {
        await axiosClient.put(apiEndpoint, {
          data: JSON.stringify(data),
        })
        setEdit(false)
        const msg = label + 'を修正しました。'
        enqueueSnackbar(msg, {
          variant: 'success',
        })
        setLoading(false)
      } catch (_error) {
        const msg = 'エラーが発生しました。'
        enqueueSnackbar(msg, {
          variant: 'error',
        })
      }
    }
  }
  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      onSubmit={handleSubmit(onSubmit)}
      my={2}
    >
      {edit ? (
        <Stack
          direction={{ md: 'row', xs: 'column' }}
          alignItems="center"
          sx={{ display: edit ? 'flex' : 'none' }}
          ref={ref}
        >
          <FormControl>
            <InputLabel>権限</InputLabel>
            <Select
              value={getValues('permission')}
              label="権限"
              onChange={(event) => {
                setValue('permission', event.target.value as Permission, {
                  shouldValidate: true,
                })
              }}
            >
              <MenuItem value={'admin'}>管理者</MenuItem>
              <MenuItem value={'subAdmin'}>副管理者</MenuItem>
              <MenuItem value={'standard'}>一般</MenuItem>
            </Select>
          </FormControl>
          <Stack direction="row">
            <Button
              sx={{
                mx: 1,
                px: 1,
                minWidth: 0,
              }}
              size="small"
              color="error"
              variant="contained"
              onClick={() => {
                setEdit(false)
              }}
            >
              <CancelIcon />
            </Button>
            <LoadingButton
              disabled={Object.keys(errors).length > 0}
              loading={loading}
              sx={{
                mx: 1,
                px: 1,
                minWidth: 0,
              }}
              size="small"
              variant="contained"
              type="submit"
            >
              <SaveAsIcon />
            </LoadingButton>
          </Stack>
        </Stack>
      ) : (
        <Stack
          direction={{ md: 'row', xs: 'column' }}
          spacing={2}
          sx={{ display: !edit ? 'flex' : 'none' }}
          alignItems={{
            md: 'center',
            xs: 'flex-start',
          }}
        >
          <Typography fontWeight="bold" variant="body2" flex={'none'}>
            {label}
          </Typography>
          <Stack direction="row" alignItems="center">
            <Typography>
              {
                {
                  admin: '管理者',
                  subAdmin: '副管理者',
                  standard: '一般',
                }[permission]
              }
            </Typography>
            <Button
              sx={{
                flex: 'none',
                mx: 1,
                p: 0.5,
                minWidth: 0,
              }}
              size="small"
              variant="contained"
              color="info"
              onClick={() => {
                setEdit(true)
              }}
            >
              <ModeEditIcon fontSize="small" />
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  )
}
